import { Box, Flex, Skeleton, Spacer, useToast } from '@chakra-ui/react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  date as commonDate,
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { DownloadWithLoanProductGQLType } from 'types/dashboard-core-gqlTypes.generated.d';

import { VerificationDownloadExtendedResponseTapesButton } from './VerificationDownloadExtendedResponseTapeButton';
import { VerificationStatusIcon } from './VerificationIcons';
import { VerificationsAccordion } from './VerificationsTemplate';
import { useVerificationOptions } from './VerificationTableSharedComponents';
import { useGetBorrowingBaseReportsQuery } from 'packages/borrowing-base/requests/borrowingBase.gql.generated';
import { BorrowingBaseSummaryWithSingleValue } from 'packages/borrowing-base/ui/borrowingBaseSummary/BorrowingBaseSummaryWithSingleValue';
import { BorrowingBaseSummarySections } from 'packages/borrowing-base/ui/borrowingBaseSummary/utils';
import { colors } from 'packages/common-frontend/colors';
import { InfoAlertWithDescription } from 'packages/common-frontend/components/InfoAlertWithDescription';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { useGetCreditFacilityQuery } from 'packages/credit-facility/requests/requests.gql.generated';
import { useGetCurrentCreditAgreementFullArtifactQuery } from 'packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';

type VerificationSingleBorrowingBaseReportPlaceholderProps = {
  reportingDate: Util.FullDateStr | null;
  status: 'inProgress' | 'notStarted';
  isExpanded: boolean;
};
export const VerificationSingleBorrowingBaseReportPlaceholder = ({
  reportingDate,
  status,
  isExpanded,
}: VerificationSingleBorrowingBaseReportPlaceholderProps) => {
  return (
    <VerificationsAccordion
      header={`View ${
        reportingDate
          ? commonDate.fullDateStrToSlashedDateStr(
              reportingDate,
              commonDate.formatSlashedDateStr,
            )
          : ''
      } Borrowing Base Report`}
      icon={<VerificationStatusIcon status={status} />}
      isExpanded={isExpanded}
    >
      <InfoAlertWithDescription description="Please wait until calculations are complete." />
    </VerificationsAccordion>
  );
};

type VerificationSingleBorrowingBaseReportProps = {
  creditFacilityId: string;
  reportingDate: Util.FullDateStr;
  fileVersion: number;
  extendedResponseTapeDownloads: DownloadWithLoanProductGQLType[] | null;
  isExpanded: boolean;
};
export const VerificationSingleBorrowingBaseReport = ({
  creditFacilityId,
  reportingDate,
  fileVersion,
  extendedResponseTapeDownloads,
  isExpanded,
}: VerificationSingleBorrowingBaseReportProps) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { navigator } = useVerificationOptions();

  const { data: creditFacilityData } = useGetCreditFacilityQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    variables: { creditFacilityId },
  });
  const formattedSlug = creditFacilityData?.creditFacility.formattedSlug;

  const { data: borrowingBaseReportsData } = useGetBorrowingBaseReportsQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    variables: { creditFacilityId, startReportingDate: reportingDate },
  });
  const borrowingBaseReport = borrowingBaseReportsData?.borrowingBaseReports.find(
    (bbr) =>
      bbr.reportingDate === reportingDate && bbr.fileVersion === fileVersion,
  );

  const {
    data: creditAgreementData,
  } = useGetCurrentCreditAgreementFullArtifactQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    variables: { creditFacilityId },
    fetchPolicy: 'network-only',
  });
  const creditAgreementString =
    creditAgreementData?.currentCreditAgreementFullArtifact?.creditAgreement;
  const creditAgreementId =
    creditAgreementData?.currentCreditAgreementFullArtifact?.id;

  let parsedCreditAgreement = null;
  if (creditAgreementString) {
    try {
      parsedCreditAgreement = parseCreditAgreementFullArtifact(
        creditAgreementString,
      );
    } catch (err) {
      toast({
        ...errorToastConfig,
        id: 'errorGetCurrentCreditAgreementFullArtifact',
        title: `Error parsing credit agreement artifact for credit agreement ${creditAgreementId}`,
      });
      navigator(navigate).reset();
    }
    finleyAssert(
      parsedCreditAgreement,
      `Expect currentCreditAgreementFullArtifact to exist for creditFacilityId ${creditFacilityId}`,
    );
  }

  return (
    <VerificationsAccordion
      header={`View ${commonDate.fullDateStrToSlashedDateStr(
        reportingDate,
        commonDate.formatSlashedDateStr,
      )} Borrowing Base Report for ${formattedSlug}`}
      icon={<VerificationStatusIcon status="notStarted" />}
      isExpanded={isExpanded}
      isExpandedBackgroundColor={colors.millet[50]}
    >
      {extendedResponseTapeDownloads && (
        <Flex mb="16px">
          <Spacer />
          <VerificationDownloadExtendedResponseTapesButton
            extendedResponseTapeDownloads={extendedResponseTapeDownloads}
          />
        </Flex>
      )}
      {formattedSlug && borrowingBaseReport && parsedCreditAgreement ? (
        <Box
          padding="24px"
          borderRadius="8px"
          border={`1px solid ${colors.gray[200]}`}
          backgroundColor="white"
        >
          <BorrowingBaseSummaryWithSingleValue
            borrowingBaseReport={borrowingBaseReport}
            creditAgreement={parsedCreditAgreement}
            sections={[BorrowingBaseSummarySections.receivables]}
          />
        </Box>
      ) : (
        <Skeleton height="600px" data-testid="verification-bbr-loading" />
      )}
    </VerificationsAccordion>
  );
};
